import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Artist } from '../types';
import { supabase } from '../lib/supabase';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  artist: Artist | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (email: string, password: string, role: 'artist' | 'admin', firstName?: string, lastName?: string) => Promise<{ success: boolean; needsVerification?: boolean; message?: string }>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth hook sadece AuthProvider içinde kullanılabilir');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [artist, setArtist] = useState<Artist | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastAuthCheck, setLastAuthCheck] = useState<number>(0);





  useEffect(() => {
    // Emergency timeout - 3 saniye sonra loading'i false yap
    const emergencyTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    // Session kontrol fonksiyonu
    const checkSession = async () => {
      const now = Date.now();
      
      // Son kontrol 30 saniye içindeyse skip et (performans optimizasyonu)
      if (now - lastAuthCheck < 30000) {
        clearTimeout(emergencyTimeout);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLastAuthCheck(now);
      
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('❌ Session kontrolü hatası:', error);
          return;
        }

        if (session?.user) {
          // Session'dan direkt user data set et
          const user = session.user;
          const userData = {
            id: user.id,
            email: user.email || '',
            role: (user.user_metadata?.role || 'artist') as 'artist' | 'admin',
            created_at: user.created_at || new Date().toISOString()
          };

          setUser(userData);

          // Artist data set et
          if (userData.role === 'artist') {
            const artistData = {
              id: user.id,
              user_id: user.id,
              first_name: user.user_metadata?.first_name || '',
              last_name: user.user_metadata?.last_name || '',
              email: user.email || '',
              display_name: user.user_metadata?.display_name || user.email?.split('@')[0] || '',
              bio: user.user_metadata?.bio || '',
              avatar: user.user_metadata?.avatar || '',
              status: 'active' as const,
              created_at: user.created_at || new Date().toISOString()
            };
            
            setArtist(artistData);
          }

        }
      } catch (error) {
        console.error('❌ Session kontrolü exception:', error);
      } finally {
        clearTimeout(emergencyTimeout);
        setIsLoading(false);
      }
    };

    checkSession();

    // Auth state değişikliklerini dinle - sadece major events için
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        // Sadece önemli auth değişikliklerinde state'i güncelle
        if (event === 'SIGNED_IN' && session?.user) {
          const user = session.user;
          const userData = {
            id: user.id,
            email: user.email || '',
            role: (user.user_metadata?.role || 'artist') as 'artist' | 'admin',
            created_at: user.created_at || new Date().toISOString()
          };

          setUser(userData);

          // Artist data set et
          if (userData.role === 'artist') {
            const artistData = {
              id: user.id,
              user_id: user.id,
              first_name: user.user_metadata?.first_name || '',
              last_name: user.user_metadata?.last_name || '',
              email: user.email || '',
              display_name: user.user_metadata?.display_name || user.email?.split('@')[0] || '',
              bio: user.user_metadata?.bio || '',
              avatar: user.user_metadata?.avatar || '',
              status: 'active' as const,
              created_at: user.created_at || new Date().toISOString()
            };
            
            setArtist(artistData);
          }

          setIsLoading(false);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setArtist(null);
          setIsLoading(false);
          setLastAuthCheck(0); // Reset auth check timestamp
        }
        // TOKEN_REFRESHED ve diğer minor events'leri ignore et
      }
    );

    return () => {
      subscription.unsubscribe();
      clearTimeout(emergencyTimeout);
    };
  }, [lastAuthCheck]);

  const register = async (email: string, password: string, role: 'artist' | 'admin', firstName?: string, lastName?: string): Promise<{ success: boolean; needsVerification?: boolean; message?: string }> => {
    setIsLoading(true);
    
    try {
      // Supabase Auth ile kullanıcı oluştur (email confirmation gerekli)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: role,
            first_name: firstName || '',
            last_name: lastName || '',
            display_name: email.split('@')[0]
          }
        }
      });

      if (error) {
        console.error('Kayıt hatası:', error);
        return { 
          success: false, 
          message: error.message 
        };
      }

      if (data.user) {
        // Email doğrulama bekleniyor
        if (!data.user.email_confirmed_at) {
          return { 
            success: true, 
            needsVerification: true,
            message: 'EMAIL_VERIFICATION_REQUIRED' 
          };
        }

        // Email zaten doğrulanmışsa (nadiren olur)
        const userData = {
          id: data.user.id,
          email: data.user.email || email,
          role: role,
          created_at: new Date().toISOString()
        };
        
        setUser(userData);

        if (role === 'artist') {
          const artistData = {
            id: data.user.id,
            user_id: data.user.id,
            first_name: firstName || '',
            last_name: lastName || '',
            email: data.user.email || email,
            display_name: email.split('@')[0],
            bio: '',
            avatar: '',
            status: 'active' as const,
            created_at: new Date().toISOString()
          };
          setArtist(artistData);
        }
      }

      return { success: true };
    } catch (error: any) {
      console.error('Kayıt hatası:', error);
      return { 
        success: false, 
        message: `Genel kayıt hatası: ${error.message || error}` 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('❌ Auth hatası:', error);
        setIsLoading(false); // Hata durumunda loading false yap
        return { 
          success: false, 
          message: error.message || 'Giriş başarısız' 
        };
      }

      if (data.user) {
        return { success: true };
      }

      setIsLoading(false); // Beklenmeyen durum
      return { 
        success: false, 
        message: 'Beklenmeyen hata' 
      };
    } catch (error: any) {
      console.error('❌ Login exception:', error);
      setIsLoading(false); // Exception durumunda loading false yap
      return { 
        success: false, 
        message: 'Giriş sırasında bir hata oluştu.' 
      };
    }
    // finally block'u kaldırıyoruz - onAuthStateChange callback'i loading'i handle edecek
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ Çıkış hatası:', error);
      }
      
      // onAuthStateChange callback'i user/artist/loading state'ini handle edecek
    } catch (error) {
      console.error('❌ Çıkış hatası:', error);
      // Hata durumunda manuel olarak temizle
      setUser(null);
      setArtist(null);
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    artist,
    login,
    register,
    logout,
    isLoading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 